import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchCategories, createCategory, updateCategory, deleteCategory, fetchNameSurcharge, updateNameSurcharge } from "../api/CategoryAPIs";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../utils/auth";
import { isOwner } from "../utils/roles";
import { colors } from "../constants/colors";
import { API_BASE_URL_V2 } from "../api/URL_Header";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  ChevronRight,
  Package,
  Edit,
  Trash2,
  Plus,
  Save,
  X,
  Image as ImageIcon,
  Layers,
  Eye,
  FolderTree,
  AlertCircle,
  AlertTriangle,
  
} from "lucide-react";

const SkeletonLoader = ({ type }) => {
  if (type === "table") {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded mb-4"></div>
        {[...Array(3)].map((_, index) => (
          <div key={index} className="flex gap-4 border-b py-3">
            <div className="w-12 h-12 bg-gray-200 rounded"></div>
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

const CategoriesPage = () => {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const owner = isOwner(authState.role);
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [deleteCategoryId, setDeleteCategoryId] = useState(null);
  const [nameSurcharge, setNameSurcharge] = useState("50");
  const [savingSurcharge, setSavingSurcharge] = useState(false);
  const [surchargeOpen, setSurchargeOpen] = useState(false);

  const { data: categoryData, isLoading, error } = useQuery({
    queryKey: ["categories"],
    queryFn: fetchCategories,
    staleTime: Infinity,
  });
  useEffect(() => {
    window.scrollTo(0, 0);
    if (!owner) return undefined;
    fetchNameSurcharge()
      .then((amount) => setNameSurcharge(String(amount)))
      .catch(() => toast.error("تعذر تحميل زيادة سعر الاسم"));
    return undefined;
  }, [owner]);

  useEffect(() => {
    if (categoryData) {
      setCategories(
        categoryData.map((cat) => ({
          id: cat.id,
          name: cat.name,
          imageUrl: cat.image,
        }))
      );
    }
  }, [categoryData]);

  const handleImageChange = (e, setState, state) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setState({
          ...state,
          imageBase64: reader.result.split(",")[1],
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleInputChange = (e, setState, state) => {
    const { name, value } = e.target;
    setState({ ...state, [name]: value });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const created = await createCategory(newCategory);
      setCategories([...categories, { id: created.CategoryId, name: created.Name, imageUrl: created.ImageUrl }]);
      setNewCategory(null);
      toast.success("تم إضافة الفئة بنجاح!");
    } catch (err) {
      console.error("Error creating category:", err);
      toast.error("فشل في إضافة الفئة!");
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const updated = await updateCategory(editingCategory.id, editingCategory);
      setCategories(categories.map((cat) => (cat.id === updated.CategoryId ? { id: updated.CategoryId, name: updated.Name, imageUrl: updated.ImageUrl } : cat)));
      setEditingCategory(null);
      toast.success("تم تحديث الفئة بنجاح!");
    } catch (err) {
      console.error("Error updating category:", err);
      toast.error("فشل في تحديث الفئة!");
    }
  };

  const handleSaveNameSurcharge = async (e) => {
    e.preventDefault();
    const amount = Number(nameSurcharge);
    if (!Number.isFinite(amount) || amount < 0) {
      toast.error("اكتب رقم صفر أو أكبر");
      return;
    }
    setSavingSurcharge(true);
    try {
      const saved = await updateNameSurcharge(amount);
      setNameSurcharge(String(saved));
      setSurchargeOpen(false);
      toast.success("تم حفظ زيادة سعر الاسم");
    } catch (err) {
      console.error("Error saving name surcharge:", err);
      toast.error("فشل حفظ زيادة سعر الاسم");
    } finally {
      setSavingSurcharge(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteCategory(deleteCategoryId);
      setCategories(categories.filter((cat) => cat.id !== deleteCategoryId));
      setDeleteCategoryId(null);
      toast.success("تم حذف الفئة بنجاح!");
    } catch (err) {
      console.error("Error deleting category:", err);
      toast.error("فشل في حذف الفئة!");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-4 md:p-8" dir="rtl">
      <ToastContainer position="top-center" />
      
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-8">
          <div className="flex items-center gap-3">
            <FolderTree className="text-purple-600" size={32} />
            <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              إدارة الفئات
            </h1>
          </div>
          <button
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2 bg-white/80 backdrop-blur-sm text-gray-700 px-6 py-3 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-200"
          >
            <ChevronRight size={20} />
            العودة للرئيسية
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-2xl mb-6 flex items-center gap-3">
            <AlertCircle className="text-red-500" size={24} />
            <p>فشل في تحميل الفئات. يرجى المحاولة مرة أخرى.</p>
          </div>
        )}

        {owner && (
          <div className="flex justify-end mb-6">
            <button
              type="button"
              onClick={() => setSurchargeOpen(true)}
              className="bg-white/80 backdrop-blur-sm text-fuchsia-700 px-5 py-3 rounded-xl shadow-md hover:shadow-xl transition border border-fuchsia-200 font-bold"
            >
              زيادة سعر الاسم
            </button>
          </div>
        )}

        {owner && surchargeOpen && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
            <form
              onSubmit={handleSaveNameSurcharge}
              className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md border border-fuchsia-200"
            >
              <h2 className="text-xl font-bold text-gray-800">زيادة سعر الاسم</h2>
              <p className="text-sm text-gray-600 mt-2">
                المبلغ ده بيتضاف لما العميل يكتب اسم على تصميم الاسم فيه اختياري. تصميم الاسم الإجباري بيفضل بسعره.
              </p>
              <label className="block text-right font-medium text-gray-700 mt-4 mb-2">
                الزيادة بالجنيه
              </label>
              <input
                type="number"
                min="0"
                step="1"
                value={nameSurcharge}
                onChange={(e) => setNameSurcharge(e.target.value)}
                className="w-full p-3 border-2 border-fuchsia-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-fuchsia-200 focus:border-fuchsia-400"
              />
              <div className="flex gap-3 mt-5">
                <button
                  type="submit"
                  disabled={savingSurcharge}
                  className="flex-1 bg-gradient-to-r from-fuchsia-600 to-pink-600 text-white px-6 py-3 rounded-xl font-bold disabled:opacity-60"
                >
                  {savingSurcharge ? "جاري الحفظ..." : "حفظ"}
                </button>
                <button
                  type="button"
                  onClick={() => setSurchargeOpen(false)}
                  className="px-6 py-3 rounded-xl border border-gray-200 font-bold text-gray-700"
                >
                  إلغاء
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Stats Card */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 mb-8 border border-white/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-purple-100 rounded-xl">
                <Layers className="text-purple-600" size={24} />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-800">عدد الفئات</h2>
                <p className="text-sm text-gray-600">إجمالي الفئات المتاحة</p>
              </div>
            </div>
            <div className="text-4xl font-bold text-purple-600">{categories.length}</div>
          </div>
        </div>

        {/* Add Category Button */}
        <div className="flex justify-end mb-6">
          <button
            onClick={() => setNewCategory({ name: "", imageBase64: "" })}
            className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-6 py-3 rounded-xl shadow-md hover:shadow-xl hover:scale-105 transition-all duration-300 flex items-center gap-2 font-bold"
          >
            <Plus size={20} />
            إضافة فئة جديدة
          </button>
        </div>

        {/* Categories Table */}
        {isLoading ? (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6">
            <SkeletonLoader type="table" />
          </div>
        ) : categories.length ? (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-white/30 overflow-x-auto">
            <table className="w-full min-w-[800px] table-auto">
              <thead>
                <tr className="bg-gradient-to-r from-purple-50 to-pink-50 border-b-2 border-purple-200">
                  <th className="p-4 text-right font-bold text-purple-800">#ID</th>
                  <th className="p-4 text-right font-bold text-purple-800">الصورة</th>
                  <th className="p-4 text-right font-bold text-purple-800">اسم الفئة</th>
                  <th className="p-4 text-center font-bold text-purple-800">التصميمات</th>
                  <th className="p-4 text-center font-bold text-purple-800">الإجراءات</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((cat) => (
                  <tr key={cat.id} className="border-b border-gray-200 hover:bg-white/60 transition-colors">
                    <td className="p-4">
                      <span className="font-mono font-bold text-purple-600">#{cat.id}</span>
                    </td>
                    <td className="p-4">
                      {cat.imageUrl ? (
                        <img
                          src={`${API_BASE_URL_V2}${cat.imageUrl}`}
                          alt={cat.name}
                          className="h-16 w-16 object-cover rounded-xl border-2 border-gray-200 shadow-sm"
                        />
                      ) : (
                        <div className="h-16 w-16 bg-gray-100 rounded-xl flex items-center justify-center border-2 border-dashed border-gray-300">
                          <ImageIcon className="text-gray-400" size={24} />
                        </div>
                      )}
                    </td>
                    <td className="p-4 font-medium">{cat.name}</td>
                    <td className="p-4 text-center">
                      <button
                        onClick={() => navigate(`/categories/${cat.id}/designs`)}
                        className="bg-gradient-to-r from-teal-500 to-emerald-500 text-white px-4 py-2 rounded-lg hover:shadow-lg hover:scale-105 transition-all duration-300 text-sm font-bold inline-flex items-center gap-2"
                      >
                        <Eye size={16} />
                        عرض التصميمات
                      </button>
                    </td>
                    <td className="p-4">
                      <div className="flex gap-2 justify-center">
                        <button
                          onClick={() =>
                            setEditingCategory({
                              id: cat.id,
                              name: cat.name,
                              imageUrl: cat.imageUrl,
                              imageBase64: "",
                            })
                          }
                          className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 hover:shadow-lg hover:scale-105 transition-all duration-300 text-sm font-bold flex items-center gap-2"
                        >
                          <Edit size={14} />
                          تعديل
                        </button>
                        <button
                          onClick={() => setDeleteCategoryId(cat.id)}
                          className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 hover:shadow-lg hover:scale-105 transition-all duration-300 text-sm font-bold flex items-center gap-2"
                        >
                          <Trash2 size={14} />
                          حذف
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-16 text-center">
            <FolderTree className="mx-auto mb-4 text-gray-400" size={64} />
            <p className="text-2xl text-gray-700 mb-2">لا توجد فئات</p>
            <p className="text-lg text-gray-500">قم بإضافة فئة جديدة للبدء</p>
          </div>
        )}

        {/* Add Category Modal */}
        {newCategory && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
              <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-pink-600 text-white p-6 rounded-t-2xl flex justify-between items-center">
                <h2 className="text-2xl font-bold">إضافة فئة جديدة</h2>
                <button
                  onClick={() => setNewCategory(null)}
                  className="hover:bg-white/20 p-2 rounded-lg transition"
                >
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleCreate} className="p-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-right font-medium text-gray-700 mb-2">
                      اسم الفئة
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={newCategory.name}
                      onChange={(e) => handleInputChange(e, setNewCategory, newCategory)}
                      className="w-full p-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-purple-300 focus:border-purple-400 transition-all"
                      placeholder="أدخل اسم الفئة"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-right font-medium text-gray-700 mb-2">
                      صورة الفئة
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-xl p-4 text-center hover:border-purple-400 transition-colors">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageChange(e, setNewCategory, newCategory)}
                        className="w-full"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-center gap-4 mt-8">
                  <button
                    type="button"
                    onClick={() => setNewCategory(null)}
                    className="flex items-center gap-2 bg-gray-400 text-white px-6 py-3 rounded-xl hover:bg-gray-500 font-bold text-lg transition"
                  >
                    <X size={20} />
                    إلغاء
                  </button>
                  <button
                    type="submit"
                    className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-3 rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300 font-bold text-lg"
                  >
                    <Save size={20} />
                    إضافة
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Category Modal */}
        {editingCategory && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
              <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 rounded-t-2xl flex justify-between items-center">
                <h2 className="text-2xl font-bold">تعديل الفئة #{editingCategory.id}</h2>
                <button
                  onClick={() => setEditingCategory(null)}
                  className="hover:bg-white/20 p-2 rounded-lg transition"
                >
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleUpdate} className="p-6">
                <div className="space-y-4">
                  {editingCategory.imageUrl && (
                    <div>
                      <label className="block text-right font-medium text-gray-700 mb-2">
                        الصورة الحالية
                      </label>
                      <img
                        src={`${API_BASE_URL_V2}${editingCategory.imageUrl}`}
                        alt="Current"
                        className="h-32 w-32 object-cover rounded-xl border-2 border-gray-200 mx-auto"
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-right font-medium text-gray-700 mb-2">
                      اسم الفئة
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={editingCategory.name}
                      onChange={(e) => handleInputChange(e, setEditingCategory, editingCategory)}
                      className="w-full p-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-blue-300 focus:border-blue-400 transition-all"
                      placeholder="أدخل اسم الفئة"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-right font-medium text-gray-700 mb-2">
                      صورة جديدة (اختياري)
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-xl p-4 text-center hover:border-blue-400 transition-colors">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageChange(e, setEditingCategory, editingCategory)}
                        className="w-full"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-center gap-4 mt-8">
                  <button
                    type="button"
                    onClick={() => setEditingCategory(null)}
                    className="flex items-center gap-2 bg-gray-400 text-white px-6 py-3 rounded-xl hover:bg-gray-500 font-bold text-lg transition"
                  >
                    <X size={20} />
                    إلغاء
                  </button>
                  <button
                    type="submit"
                    className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-8 py-3 rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300 font-bold text-lg"
                  >
                    <Save size={20} />
                    حفظ التغييرات
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteCategoryId && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
              <div className="bg-gradient-to-r from-red-600 to-pink-600 text-white p-6 rounded-t-2xl">
                <h2 className="text-2xl font-bold text-center">تأكيد الحذف</h2>
              </div>

              <div className="p-6 text-center">
                <AlertTriangle className="mx-auto mb-4 text-red-500" size={64} />
                <p className="text-xl text-gray-700 mb-2">
                  هل أنت متأكد من حذف الفئة رقم #{deleteCategoryId}؟
                </p>
                <p className="text-gray-500 mb-6">
                  هذا الإجراء لا يمكن التراجع عنه. سيتم حذف الفئة نهائياً.
                </p>

                <div className="flex justify-center gap-4">
                  <button
                    type="button"
                    onClick={() => setDeleteCategoryId(null)}
                    className="flex items-center gap-2 bg-gray-400 text-white px-6 py-3 rounded-xl hover:bg-gray-500 font-bold text-lg transition"
                  >
                    <X size={20} />
                    إلغاء
                  </button>
                  <button
                    onClick={handleDelete}
                    className="flex items-center gap-2 bg-gradient-to-r from-red-600 to-pink-600 text-white px-8 py-3 rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300 font-bold text-lg"
                  >
                    <Trash2 size={20} />
                    حذف
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoriesPage;